import React, { useCallback, useEffect, useRef, useState } from "react";
import styled from "styled-components";
import {
  AgreementError,
  agreementRequest,
  documentDigest,
  RentalAgreement as Agreement,
} from "./api";

const Card = styled.section`
  width: 100%;
  box-sizing: border-box;
  margin: 18px 0;
  padding: 20px;
  background: #fff;
  border: 1px solid #ded9ea;
  border-radius: 16px;
  color: #252136;
  text-align: left;
  h2 {
    font-size: 18px;
    margin: 0 0 8px;
  }
  p {
    font-size: 14px;
    line-height: 1.5;
  }
  button,
  .document-link {
    border: 1px solid #6941c6;
    border-radius: 9px;
    padding: 10px 16px;
    color: #fff;
    background: #6941c6;
    cursor: pointer;
    font: inherit;
  }
  button:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
  .secondary {
    background: #fff;
    color: #6941c6;
  }
  .actions {
    display: flex;
    flex-wrap: wrap;
    gap: 10px;
    margin-top: 14px;
  }
  label {
    display: block;
    font-size: 14px;
    margin: 14px 0;
    line-height: 1.5;
  }
  input[type="text"] {
    display: block;
    width: 100%;
    box-sizing: border-box;
    margin-top: 6px;
    border: 1px solid #9a92a7;
    border-radius: 8px;
    padding: 12px;
    font: inherit;
  }
  input[type="checkbox"] {
    margin-right: 8px;
  }
  canvas {
    display: block;
    width: 100%;
    height: 160px;
    background: #fff;
    border: 1px dashed #9a92a7;
    border-radius: 8px;
    touch-action: none;
  }
  iframe {
    width: 100%;
    height: 430px;
    border: 1px solid #ded9ea;
    margin-top: 16px;
  }
  .signature {
    max-width: 100%;
    max-height: 120px;
  }
  .signed {
    color: #18784d;
    font-weight: 600;
  }
  [role="alert"] {
    color: #a52e3d;
  }
`;

export default function RentalAgreement({
  reservationUid,
}: {
  reservationUid: string;
}) {
  const [agreement, setAgreement] = useState<Agreement | null>(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [opened, setOpened] = useState(false);
  const [pdfUrl, setPdfUrl] = useState("");
  const [name, setName] = useState("");
  const [consent, setConsent] = useState(false);
  const [drawn, setDrawn] = useState(false);
  const canvas = useRef<HTMLCanvasElement>(null);
  const drawing = useRef(false);
  const requestVersion = useRef(0);

  const load = useCallback(async () => {
    const version = ++requestVersion.current;
    setError("");
    try {
      const value = await (await agreementRequest(reservationUid)).json();
      if (version === requestVersion.current) {
        setAgreement(value);
      }
    } catch (e) {
      if (version !== requestVersion.current) {
        return;
      }
      setAgreement(null);
      if (!(e instanceof AgreementError && e.status === 404)) {
        setError((e as Error).message);
      }
    }
  }, [reservationUid]);

  useEffect(() => {
    setAgreement(null);
    setOpened(false);
    setPdfUrl("");
    setConsent(false);
    setName("");
    setDrawn(false);
    load();
    const version = requestVersion.current;
    return () => {
      requestVersion.current = version + 1;
    };
  }, [load]);
  useEffect(
    () => () => {
      if (pdfUrl) {
        URL.revokeObjectURL(pdfUrl);
      }
    },
    [pdfUrl]
  );

  const clear = () => {
    const target = canvas.current;
    if (target) {
      target.getContext("2d")?.clearRect(0, 0, target.width, target.height);
    }
    drawing.current = false;
    setDrawn(false);
  };

  const hideAgreement = () => {
    setAgreement(null);
    setOpened(false);
    setPdfUrl("");
    setConsent(false);
    clear();
    setError("");
  };

  const open = async () => {
    const version = requestVersion.current;
    setLoading(true);
    setError("");
    setPdfUrl("");
    setConsent(false);
    clear();
    try {
      const current: Agreement = await (
        await agreementRequest(reservationUid)
      ).json();
      const response = await agreementRequest(reservationUid, "/document");
      const bytes = await response.arrayBuffer();
      if ((await documentDigest(bytes)) !== current.documentHash) {
        throw new Error(
          "The document was updated. Open it again before signing."
        );
      }
      if (version !== requestVersion.current) {
        return;
      }
      setAgreement(current);
      setPdfUrl(
        URL.createObjectURL(new Blob([bytes], { type: "application/pdf" }))
      );
      setOpened(true);
    } catch (e) {
      if (version === requestVersion.current) {
        if (e instanceof AgreementError && e.status === 404) {
          hideAgreement();
        } else {
          setError((e as Error).message);
        }
      }
    } finally {
      if (version === requestVersion.current) {
        setLoading(false);
      }
    }
  };

  const position = (event: React.PointerEvent<HTMLCanvasElement>) => {
    const box = event.currentTarget.getBoundingClientRect();
    return [
      ((event.clientX - box.left) * event.currentTarget.width) / box.width,
      ((event.clientY - box.top) * event.currentTarget.height) / box.height,
    ];
  };
  const startDrawing = (event: React.PointerEvent<HTMLCanvasElement>) => {
    if (loading) {
      return;
    }
    const ctx = event.currentTarget.getContext("2d");
    if (!ctx) {
      return;
    }
    event.currentTarget.setPointerCapture(event.pointerId);
    const [x, y] = position(event);
    ctx.beginPath();
    ctx.moveTo(x, y);
    ctx.lineWidth = 3;
    ctx.lineCap = "round";
    ctx.strokeStyle = "#252136";
    drawing.current = true;
  };
  const draw = (event: React.PointerEvent<HTMLCanvasElement>) => {
    if (!drawing.current || loading) {
      return;
    }
    const [x, y] = position(event);
    const ctx = event.currentTarget.getContext("2d");
    ctx?.lineTo(x, y);
    ctx?.stroke();
    setDrawn(true);
  };

  const submit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (
      !agreement?.canSign ||
      !pdfUrl ||
      !consent ||
      !drawn ||
      !canvas.current ||
      loading ||
      name.trim().length < 2
    ) {
      return;
    }
    const version = requestVersion.current;
    setLoading(true);
    setError("");
    try {
      const signed = await (
        await agreementRequest(reservationUid, "/sign", {
          contractUid: agreement.contractUid,
          documentHash: agreement.documentHash,
          signerName: name.trim(),
          consentAccepted: true,
          signatureImage: canvas.current.toDataURL("image/png"),
        })
      ).json();
      if (version !== requestVersion.current) {
        return;
      }
      setAgreement(signed);
      setConsent(false);
      clear();
    } catch (e) {
      if (version !== requestVersion.current) {
        return;
      }
      if (e instanceof AgreementError && e.status === 404) {
        hideAgreement();
        return;
      }
      if (e instanceof AgreementError && e.status === 409) {
        setOpened(false);
        setPdfUrl("");
        setConsent(false);
        clear();
        await load();
      }
      setError((e as Error).message);
    } finally {
      if (version === requestVersion.current) {
        setLoading(false);
      }
    }
  };

  const downloadReceipt = async () => {
    const version = requestVersion.current;
    setLoading(true);
    setError("");
    try {
      const response = await agreementRequest(reservationUid, "/receipt");
      const bytes = await response.blob();
      if (version !== requestVersion.current) {
        return;
      }
      const url = URL.createObjectURL(bytes);
      const link = document.createElement("a");
      link.href = url;
      link.download = "rental-agreement-signature.pdf";
      document.body.appendChild(link);
      link.click();
      link.remove();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch (e) {
      if (version === requestVersion.current) {
        if (e instanceof AgreementError && e.status === 404) {
          hideAgreement();
        } else {
          setError((e as Error).message);
        }
      }
    } finally {
      if (version === requestVersion.current) {
        setLoading(false);
      }
    }
  };

  if (!agreement && !error) {
    return null;
  }
  return (
    <Card aria-label="Rental agreement">
      <h2>Rental agreement</h2>
      {agreement && (
        <>
          <p>{agreement.name}</p>
          <p className={agreement.signature ? "signed" : undefined}>
            {agreement.signature
              ? `Signed by ${agreement.signature.signerName} on ${new Date(
                  agreement.signature.signedAt
                ).toLocaleString()}`
              : "Review your rental agreement and sign it before your stay."}
          </p>
          <div className="actions">
            <button type="button" onClick={open} disabled={loading}>
              {loading ? "Please wait…" : "Open agreement"}
            </button>
            {agreement.signature && (
              <button
                type="button"
                className="secondary"
                onClick={downloadReceipt}
                disabled={loading}
              >
                Download signature receipt
              </button>
            )}
          </div>
          {opened && pdfUrl && (
            <>
              <iframe title="Rental agreement PDF" src={pdfUrl} />
              <p>
                <a href={pdfUrl} download="rental-agreement.pdf">
                  Download agreement PDF
                </a>
              </p>
              {agreement.signature ? (
                <img
                  className="signature"
                  src={agreement.signature.signatureImage}
                  alt="Your saved signature"
                />
              ) : agreement.canSign ? (
                <form onSubmit={submit}>
                  <label>
                    Full name
                    <input
                      type="text"
                      autoComplete="name"
                      value={name}
                      maxLength={200}
                      required
                      disabled={loading}
                      onChange={(e) => setName(e.target.value)}
                    />
                  </label>
                  <label htmlFor="rental-signature">Draw your signature</label>
                  <canvas
                    id="rental-signature"
                    aria-label="Draw your signature"
                    ref={canvas}
                    width={900}
                    height={240}
                    onPointerDown={startDrawing}
                    onPointerMove={draw}
                    onPointerUp={() => {
                      drawing.current = false;
                    }}
                    onPointerCancel={() => {
                      drawing.current = false;
                    }}
                  />
                  <div className="actions">
                    <button
                      className="secondary"
                      type="button"
                      onClick={clear}
                      disabled={loading}
                    >
                      Clear signature
                    </button>
                  </div>
                  <label>
                    <input
                      type="checkbox"
                      checked={consent}
                      disabled={loading}
                      onChange={(e) => setConsent(e.target.checked)}
                    />
                    {agreement.consentText}
                  </label>
                  <button
                    type="submit"
                    disabled={
                      loading || !drawn || !consent || name.trim().length < 2
                    }
                  >
                    Sign agreement
                  </button>
                </form>
              ) : (
                <p>
                  Signing is available to the primary guest on an active
                  reservation.
                </p>
              )}
            </>
          )}
        </>
      )}
      {error && (
        <>
          <p role="alert">{error}</p>
          {!agreement && (
            <button type="button" onClick={load}>
              Retry
            </button>
          )}
        </>
      )}
      {agreement?.signature && (
        <p role="status">
          Your electronic signature has been recorded. Keep the PDF and
          signature receipt together.
        </p>
      )}
    </Card>
  );
}
